// Batched Contractor & Commission pay runs: group approved payments into a run,
// then validate → approve (dual control) → lock → pay → reconcile, with a
// balanced WHT GL journal. Reuses the payroll approval + audit patterns.
import { ContractorAccountingPanel } from './ContractorAccountingPanel';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Layers, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Input } from '@/features/shared/components/ui/input';
import { commissionApi, type CommissionRun, type CommissionLine, type GLJournal } from '../api/commissionApi';
import { getApiErrorMessage } from '@/lib/apiError';

const RUN_STATUS: Record<string, 'secondary' | 'warning' | 'info' | 'success' | 'danger'> = {
  draft: 'secondary', validated: 'info', approved: 'warning', locked: 'warning',
  payment_pending: 'info', paid: 'success', failed: 'danger', voided: 'secondary',
};
const money = (v: string, ccy: string) => `${ccy} ${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

// The single contextual action for a run's current status.
const NEXT: Record<string, { action: 'validate' | 'approve' | 'lock' | 'pay' | 'reconcile'; label: string; approver?: boolean }> = {
  draft: { action: 'validate', label: 'Validate' },
  validated: { action: 'approve', label: 'Approve', approver: true },
  approved: { action: 'lock', label: 'Lock', approver: true },
  locked: { action: 'pay', label: 'Pay', approver: true },
  payment_pending: { action: 'reconcile', label: 'Reconcile' },
  failed: { action: 'pay', label: 'Retry pay', approver: true },
};

export function RunsPanel({ approvedLines, canApprove, onChanged }: {
  approvedLines: CommissionLine[]; canApprove: boolean; onChanged: () => void;
}) {
  const [runs, setRuns] = useState<CommissionRun[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [nr, setNr] = useState({ name: '', country_code: 'TZ', currency: 'TZS' });
  const [expanded, setExpanded] = useState<string | null>(null);
  const [gl, setGl] = useState<GLJournal | null>(null);

  const refresh = async () => { try { setRuns(await commissionApi.listRuns()); } catch { setRuns([]); } };
  useEffect(() => { refresh(); }, []);

  const create = async () => {
    if (!nr.name) { toast.error('Name the run'); return; }
    setBusy('new');
    try { await commissionApi.createRun(nr); setCreating(false); setNr({ ...nr, name: '' }); await refresh(); }
    catch (e: any) { toast.error(getApiErrorMessage(e, 'Could not create run')); }
    finally { setBusy(null); }
  };

  const act = async (id: string, fn: () => Promise<unknown>, ok: string) => {
    setBusy(id);
    try { await fn(); toast.success(ok); await refresh(); onChanged(); }
    catch (e: any) { toast.error(getApiErrorMessage(e, 'Action failed')); }
    finally { setBusy(null); }
  };

  const attach = (run: CommissionRun) => {
    const ids = approvedLines.filter((l) => l.status === 'approved' && l.currency === run.currency).map((l) => l.line_id);
    if (!ids.length) { toast.error('No approved lines in this currency to add'); return; }
    return act(run.id, async () => { const r = await commissionApi.attachLines(run.id, ids); toast.success(`Added ${r.attached} line(s)`); }, 'Lines added');
  };

  const showGL = async (id: string) => {
    setExpanded(expanded === id ? null : id);
    if (expanded !== id) { try { setGl(await commissionApi.getRunGL(id)); } catch { setGl(null); } }
  };

  if (runs === null) return null;

  return (
    <Card>
      <CardContent className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground"><Layers className="h-4 w-4" /> Pay runs</div>
          <Button size="sm" variant="outline" onClick={() => setCreating(!creating)}>New pay run</Button>
        </div>
        {creating && (
          <div className="mb-3 flex flex-wrap items-end gap-2 rounded-md border border-border p-3">
            <Input placeholder="Run name (e.g. July agents)" value={nr.name} onChange={(e) => setNr({ ...nr, name: e.target.value })} className="w-56" />
            <Input placeholder="Country" value={nr.country_code} onChange={(e) => setNr({ ...nr, country_code: e.target.value })} className="w-20" />
            <Input placeholder="Currency" value={nr.currency} onChange={(e) => setNr({ ...nr, currency: e.target.value })} className="w-24" />
            <Button size="sm" onClick={create} disabled={busy === 'new'}>Create</Button>
          </div>
        )}
        {runs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No pay runs yet. Create one to batch approved contractor payments.</p>
        ) : (
          <div className="space-y-2">
            {runs.map((run) => {
              const next = NEXT[run.status];
              const canDoNext = next && (!next.approver || canApprove);
              return (
                <div key={run.id} className="rounded-md border border-border">
                  <div className="flex flex-wrap items-center justify-between gap-2 p-3">
                    <div>
                      <div className="font-medium text-foreground">{run.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {run.line_count} line(s) · gross {money(run.gross_total, run.currency)} · WHT {money(run.wht_total, run.currency)} · net {money(run.net_total, run.currency)}
                        {run.void_reason && ` · voided: ${run.void_reason}`}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Badge variant={RUN_STATUS[run.status] ?? 'secondary'}>{run.status.replace(/_/g, ' ')}</Badge>
                      {(run.status === 'draft' || run.status === 'validated') && (
                        <Button size="sm" variant="ghost" disabled={busy === run.id} onClick={() => attach(run)}>Add approved</Button>
                      )}
                      {canDoNext && (
                        <Button size="sm" variant="outline" disabled={busy === run.id}
                          onClick={() => act(run.id, () => commissionApi.runAction(run.id, next!.action), `${next!.label} done`)}>{next!.label}</Button>
                      )}
                      {(run.status === 'approved' || run.status === 'locked' || run.status === 'payment_pending' || run.status === 'paid' || run.status === 'failed') && (
                        <Button size="sm" variant="ghost" onClick={() => showGL(run.id)}>
                          {expanded === run.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />} GL
                        </Button>
                      )}
                    </div>
                  </div>
                  {expanded === run.id && ['approved', 'locked', 'payment_pending', 'paid'].includes(run.status) && (
                    <ContractorAccountingPanel runId={run.id} legalEntityId={run.legal_entity_id} />
                  )}
                  {expanded === run.id && gl && (
                    <div className="border-t border-border p-3 text-sm">
                      <div className="mb-1 font-medium">GL journal {gl.balanced ? <span className="text-success">· balanced</span> : <span className="text-warning">· NOT balanced</span>}</div>
                      {gl.entries.map((e, i) => (
                        <div key={i} className="flex justify-between tabular-nums">
                          <span className="text-muted-foreground">{e.account_code} {e.account}</span>
                          <span>{e.dr_cr} {money(e.amount, gl.currency)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
